import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { fetchSessionHold, type SessionData } from '@/lib/api';
import { formatMoney } from '@/lib/money';
import { holdCopy } from '@/lib/holdCopy';

export function SessionHoldPanel({ session }: { session?: SessionData | null }) {
  const { i18n } = useTranslation();
  const active = !!session && ['pending', 'active', 'charging', 'suspended'].includes(session.status);
  const eligible = !!session?.pre_auth_payment_intent_id && !!session.pre_auth_amount_cents && session.billed_to !== 'fleet';
  const query = useQuery({ queryKey: ['session-hold', session?._id],
    queryFn: () => fetchSessionHold(session!._id), enabled: eligible && !active,
    refetchInterval: 30_000, staleTime: 20_000 });
  if (!eligible || !session) return null;
  const c = holdCopy(i18n.language);
  const facts = query.data;
  const money = (cents: number) => formatMoney(cents, facts?.currency ?? session.cost.currency, i18n.language);
  return <section aria-label={c.hold} style={{ padding: 16, marginBlock: 12, border: '1px solid var(--evi-border)',
    borderRadius: 16, background: 'var(--evi-surface)', color: 'var(--evi-text)', overflowWrap: 'anywhere' }}>
    <strong>{money(session.pre_auth_amount_cents!)} {c.hold}</strong>
    {active ? <><p>{c.active}</p><p>{c.explanation}</p></> : <>
      <p>{c.original}: <strong>{money(session.pre_auth_amount_cents!)}</strong></p>
      {facts?.state === 'settled' && facts.captured_cents !== null
        ? <p>{c.final}: <strong>{money(facts.captured_cents)}</strong></p>
        : <p role="status">{facts?.state === 'failed' ? c.failed : c.pending}</p>}
      {facts?.released_cents != null && <p>{c.released}: <strong>{money(facts.released_cents)}</strong></p>}
      {!!facts?.refunded_cents && <p>{c.refunded}: <strong>{money(facts.refunded_cents)}</strong></p>}
    </>}
    <small>{c.bank}</small>
  </section>;
}
